import PropTypes from "prop-types";
import { useEffect, useMemo, useState } from "react";
import { useJobCategoryContext } from "api/settings/job-category/JobCategoryProvider";
import { useJobContext } from "api/settings/job/JobProvider";
import { useUserContext } from "api/user/UserProvider";
import { FormattedMessage } from "react-intl";
import { HandleModaleBO } from "components/bo/HandleModaleBO";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { TableManager } from "components/bo/admin-table/TableManager";
import { ACTIONS, ENTITIES, VAR_TYPES } from "constants/constants";
import { navigateEdit, navigateToParent, navigateView } from "utils/boHelper";
import { getJobCategoryOptions, translateHelper } from "utils/helper";
import Button from "core/buttons/button/Button";

export default function AdminJobs({ action }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const { lang } = useUserContext();
  const { jobCategoryById, jobCategoryList, jobCategoryListGetter } =
    useJobCategoryContext();
  const {
    jobByIdGetter,
    jobCreate,
    jobDeleteById,
    jobList,
    jobListGetter,
    jobPerId,
    jobUpdateById,
  } = useJobContext();
  // The category is a property OF THE JOB (redmine 12940), no longer an
  // attachment managed elsewhere: the back end refuses a job without one (NOT
  // NULL column). So it is entered here, and required. When it is undecided the
  // answer is AUTRES — a deliberate choice like any other, not an absence.
  // Blank first option, like the other BO dropdown screens: without it the
  // select would display the first category while the form holds no value, and
  // would block on save without anything appearing to be missing.
  const jobCategoryChoices = useMemo(
    () => [
      { name: "", value: null },
      ...(getJobCategoryOptions({ lang, list: jobCategoryList }) || []).map(
        (category) => ({
          name: category.label,
          value: category.value,
        })
      ),
    ],
    [jobCategoryList, lang]
  );

  const jobForm = [
    { col: "en", mandatory: true, isMissingValue: null },
    { col: "fr", mandatory: true, isMissingValue: null },
    {
      col: "category_id",
      mandatory: true,
      isMissingValue: null,
      type: VAR_TYPES.DROPDOWN,
      // The native select hands back a string; the column is an integer NOT
      // NULL, and the Rust deserializer refuses `"3"`. Same as the currency
      // dropdown of AdminCountries.
      toBeParsed: true,
      choices: jobCategoryChoices,
    },
  ];

  const [deleteData, setDeleteData] = useState(null);

  useEffect(() => {
    jobListGetter();
  }, [jobListGetter]);

  // The category referential feeds both the listing column and the form
  // dropdown, so it is loaded whatever the action is.
  useEffect(() => {
    jobCategoryListGetter();
  }, [jobCategoryListGetter]);

  const jobListWithCategories = useMemo(
    () =>
      jobList.map((job) => {
        const category = jobCategoryById[job.category_id];
        return {
          ...job,
          category: category
            ? translateHelper({ lang, fr: category.fr, en: category.en })
            : "",
        };
      }),
    [jobList, jobCategoryById, lang]
  );

  useEffect(() => {
    if (id) {
      jobByIdGetter(id);
    }
  }, [id]);

  const handleDelete = async (jobId) =>
    setDeleteData(jobList.find((e) => e.id === jobId));

  const validateDelete = async (jobId) => {
    try {
      await jobDeleteById(jobId);
    } catch (error) {
      console.error(error);
    }
  };
  const handleCreate = (payload) => {
    try {
      jobCreate(payload);
      navigateToParent(navigate, location);
    } catch (e) {
      console.error(e);
    }
  };
  const handleEdit = (payload) => {
    try {
      jobUpdateById(payload.id, payload);
      navigateToParent(navigate, location);
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <div>
      {action === ACTIONS.LIST && (
        <div className="flex">
          <Button
            className="btn-hover-color"
            size="large"
            color="blue"
            onClick={() => navigateView("new", navigate, location)}
          >
            <FormattedMessage
              id={"admin_create_new"}
              defaultMessage={"Nouveau"}
            />
          </Button>
        </div>
      )}
      <br />
      <TableManager
        action={action}
        entity={ENTITIES.JOBS}
        entityPerId={jobPerId}
        list={jobListWithCategories}
        inputs={jobForm}
        navigateEdit={(propsId) => navigateEdit(propsId, navigate, location)}
        navigateView={(propsId) => navigateView(propsId, navigate, location)}
        handleCreate={handleCreate}
        handleDelete={handleDelete}
        handleEdit={handleEdit}
      />
      <div id="modalDelete">
        <HandleModaleBO
          setDeleteData={setDeleteData}
          validateDelete={validateDelete}
          deleteData={deleteData}
          entity={ENTITIES.JOBS}
        />
      </div>
    </div>
  );
}
